import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { ICON_GROUPS, iconMap } from "@/lib/iconMap";
import { cn } from "@/lib/utils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentIcon: string;
  onSelect: (icon: string) => void;
};

export function IconPickerModal({
  open,
  onOpenChange,
  currentIcon,
  onSelect,
}: Props) {
  const [search, setSearch] = useState("");

  const filteredGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return ICON_GROUPS;
    return ICON_GROUPS.map((g) => ({
      ...g,
      icons: g.icons.filter((i) => i.toLowerCase().includes(term)),
    })).filter((g) => g.icons.length > 0);
  }, [search]);

  function handlePick(icon: string) {
    onSelect(icon);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Choose an icon</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            strokeWidth={1.5}
          />
          <Input
            autoFocus
            placeholder="Search icons..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="max-h-[420px] space-y-4 overflow-y-auto pr-1">
          {filteredGroups.map((group) => (
            <div key={group.label}>
              <div
                className="mb-2 text-[10px] font-semibold uppercase tracking-wide"
                style={{ color: "hsl(215,16%,47%)" }}
              >
                {group.label}
              </div>
              <div className="grid grid-cols-6 gap-2">
                {group.icons.map((name) => {
                  const Icon = iconMap[name];
                  if (!Icon) return null;
                  const selected = name === currentIcon;
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => handlePick(name)}
                      className={cn(
                        "flex flex-col items-center justify-center gap-1 rounded-md border px-1 py-2 transition-colors hover:bg-[hsl(210,50%,96%)]",
                        selected
                          ? "border-[hsl(210,100%,50%)] ring-2 ring-[hsl(210,100%,50%)]/40"
                          : "border-border",
                      )}
                      title={name}
                    >
                      <Icon className="h-5 w-5" strokeWidth={1.5} />
                      <span
                        className="truncate text-[9px] leading-none text-muted-foreground"
                        style={{ maxWidth: "60px" }}
                      >
                        {name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {filteredGroups.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No icons match "{search}".
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
